import { Icon } from "@captions-web/icons";
import { pickKeysWithRest } from "@captions-web/utils/pick-by";
import type { LinkComponentProps } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { forwardRef, Fragment, useState } from "react";

import { css, cva, cx, type RecipeVariantProps } from "~/styled-system/css";

import { CircularProgress } from "./circular-progress";
import { Spinner } from "./spinner";
import { Tooltip, TooltipProps } from "./tooltip";

type BaseButtonProps = ButtonContentProps &
  RecipeVariantProps<typeof buttonStyles> & {
    tooltip?: Omit<TooltipProps, "children">;
    disabledTooltip?: Omit<TooltipProps, "children">;
  };

function WrapComponent({
  children,
  tooltip,
}: {
  children: React.ReactNode;
  tooltip?: Omit<TooltipProps, "children">;
}) {
  if (tooltip) {
    return (
      <Tooltip {...tooltip} side={tooltip.side ?? "bottom"}>
        {children}
      </Tooltip>
    );
  }

  return <Fragment>{children}</Fragment>;
}

// TODO: remove once we migrate back to React 19
export const Button = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & BaseButtonProps
>(function Button(
  { icon, iconPosition, children, className, disabled, tooltip, disabledTooltip, ...props },
  ref
) {
  const [styleProps, rest] = pickKeysWithRest(props, buttonStyles.variantKeys);
  const [contentProps, buttonRest] = pickKeysWithRest(rest, [
    ...buttonContentStyles.variantKeys,
    "progress",
  ]);

  return (
    <WrapComponent tooltip={disabled ? disabledTooltip ?? tooltip : tooltip}>
      <button
        ref={ref}
        className={cx(
          buttonStyles({
            ...styleProps,
            rounded: styleProps.rounded ?? sizeToRounded(contentProps.size ?? "sm"),
          }),
          className
        )}
        disabled={disabled || props.isPending}
        {...buttonRest}
      >
        <ButtonContent icon={icon} iconPosition={iconPosition} {...contentProps}>
          {children}
        </ButtonContent>
      </button>
    </WrapComponent>
  );
});

/**
 * automatically puts the button into the pending state while the onClick promise is resolving
 */
export const AsyncActionButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    Omit<BaseButtonProps, "isPending"> & { onClick: () => Promise<void> }
>(function AsyncActionButton({ onClick, ...props }, ref) {
  const [isPending, setIsPending] = useState(false);

  return (
    <WrapComponent
      tooltip={props.disabled ? props.disabledTooltip ?? props.tooltip : props.tooltip}
    >
      <Button
        // @ts-expect-error - ref types are being dumb
        ref={ref}
        {...props}
        isPending={isPending}
        onClick={() => {
          setIsPending(true);
          onClick().finally(() => setIsPending(false));
        }}
      />
    </WrapComponent>
  );
});

export const LinkButton = forwardRef<
  HTMLAnchorElement,
  Omit<LinkComponentProps, "children"> & React.PropsWithChildren<BaseButtonProps>
>(function LinkButton(
  { icon, iconPosition, children, className, disabled, tooltip, disabledTooltip, ...props },
  ref
) {
  const [styleProps, rest] = pickKeysWithRest(props, buttonStyles.variantKeys);
  const [contentProps, linkRest] = pickKeysWithRest(rest, buttonContentStyles.variantKeys);

  const LinkComponent = props.to ? Link : "a";

  return (
    <WrapComponent tooltip={disabled ? disabledTooltip ?? tooltip : tooltip}>
      <LinkComponent
        ref={ref}
        className={cx(
          buttonStyles({
            ...styleProps,
            rounded: styleProps.rounded ?? sizeToRounded(contentProps.size ?? "sm"),
          }),
          className
        )}
        disabled={disabled || props.isPending}
        aria-disabled={disabled || props.isPending}
        {...linkRest}
      >
        <ButtonContent icon={icon} iconPosition={iconPosition} {...contentProps}>
          {children}
        </ButtonContent>
      </LinkComponent>
    </WrapComponent>
  );
});

type ButtonContentProps = RecipeVariantProps<typeof buttonContentStyles> & {
  icon?: React.ReactNode;
  /**
   * @default "before"
   */
  iconPosition?: "before" | "after" | "only";
  progress?: number;
};

function ButtonContent({
  icon,
  iconPosition = "before",
  isPending,
  size,
  children,
  progress,
}: React.PropsWithChildren<ButtonContentProps>) {
  return (
    <Fragment>
      <div className={buttonContentStyles({ size, iconOnly: iconPosition === "only", isPending })}>
        {iconPosition === "before" && icon}
        {iconPosition === "only" ? icon : children}
        {iconPosition === "after" && icon}
      </div>
      {isPending && (
        <div className={css({ position: "absolute", lineHeight: 0 })}>
          {typeof progress !== "number" && <Spinner size={16} />}
          {typeof progress === "number" && (
            <CircularProgress size="sm" progress={progress} className={circularProgressStyles} />
          )}
        </div>
      )}
    </Fragment>
  );
}

export interface PlayButtonProps {
  isPlaying: boolean;
  isPending?: boolean;
  onTogglePlay: () => Promise<void>;
  size?: "sm" | "md";
  tooltip?: Omit<TooltipProps, "children">;
}

export const PlayButton = forwardRef<HTMLButtonElement, PlayButtonProps>(function PlayButton(
  { isPlaying, isPending, onTogglePlay, size = "md", tooltip },
  ref
) {
  return (
    <WrapComponent tooltip={tooltip}>
      <Button
        ref={ref}
        variant="secondary"
        size={size}
        icon={<Icon name={isPlaying ? "pause-button" : "play-button"} />}
        iconPosition="only"
        onClick={onTogglePlay}
        rounded="full"
        isPending={isPending}
      />
    </WrapComponent>
  );
});

export const CustomColorButton = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & Omit<BaseButtonProps, "variant"> & { customColor: string }
>(function CustomColorButton({ customColor: color, style, className, ...props }, ref) {
  return (
    <Button
      // @ts-expect-error: ref
      ref={ref}
      style={color ? { ["--custom-color" as keyof React.CSSProperties]: color, ...style } : style}
      className={cx(customButtonStyles, className)}
      {...props}
    />
  );
});

const customButtonStyles = css({
  background: "var(--custom-color) !important",
  color: "white",
  boxShadow: "token(shadows.$highlight)",
  "&:disabled": {
    color: "white",
    opacity: 0.4,
  },
  "&:not(:disabled):not([aria-disabled=true]):hover": {
    color: "white",
    background: "color-mix(in srgb, var(--custom-color), white 10%) !important",
  },
  "&:focus-visible:after": {
    background: "var(--custom-color) !important",
  },
});

export const buttonStyles = cva({
  base: {
    flexShrink: 0,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    border: "none",
    cursor: "pointer",
    outline: "none",

    "& > *": {
      pointerEvents: "none",
    },

    "&:disabled, &[aria-disabled=true]": {
      cursor: "not-allowed",
      boxShadow: "none",
      // pointerEvents: "none",
    },

    "&:focus-visible:after": {
      content: '""',
      position: "absolute",
      inset: -3,
      border: "1.5px solid transparent",
      mask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
    },
  },

  variants: {
    variant: {
      primary: {
        background: "token(gradients.$purple.primary)",
        color: "white",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          color: "white",
          background: "token(gradients.$purple.hover)",
        },

        "&:focus-visible:after": {
          background: "token(gradients.$purple.primary)",
        },
      },
      secondary: {
        background: "$gray.900",
        color: "$gray.200",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          background: "$gray.800",
          color: "$gray.200",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
      tertiary: {
        color: "$gray.200",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          background: "$gray.800",
          color: "$gray.200",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
      quarternary: {
        color: "$gray.300",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          color: "$gray.100",
        },

        "&:disabled, &[aria-disabled=true]": {
          background: "transparent",
          color: "$gray.600",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
      blur: {
        backdropFilter: "blur(64px)",
        backgroundColor: "$surface.translucent",
        color: "white",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          backgroundColor: "$surface.highlight",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
      elevated: {
        backgroundColor: "$surface.highlight",
        color: "white",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          backgroundColor: "rgba(255, 255, 255, 0.15)",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
      destructive: {
        background: "$error",
        color: "$gray.10",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          color: "white",
          background: "$error.hover",
        },

        "&:focus-visible:after": {
          background: "$error",
        },
      },
      "secondary-destructive": {
        background: "$gray.900",
        color: "$error",
        boxShadow: "token(shadows.$highlight)",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          background: "$error/10",
        },

        "&:focus-visible:after": {
          background: "$surface.highlight",
        },
      },
      overlay: {
        backgroundColor: "$surface.overlay",
        color: "white",

        "&:not(:disabled):not([aria-disabled=true]):hover": {
          backgroundColor: "$surface.translucent",
        },

        "&:focus-visible:after": {
          background: "token(colors.$surface.highlight)",
        },
      },
    },
    fullWidth: {
      true: {
        width: "100%",
      },
    },
    nonInteractive: {
      true: {
        pointerEvents: "none",
      },
    },
    rounded: {
      sm: {
        borderRadius: "6px",
        "&:focus-visible:after": {
          borderRadius: "9px",
        },
      },
      md: {
        borderRadius: "8px",
        "&:focus-visible:after": {
          borderRadius: "11px",
        },
      },
      full: {
        borderRadius: "99999px",
        "&:focus-visible:after": {
          borderRadius: "99999px",
        },
      },
    },
    overBlurredSurface: {
      true: {
        // special disabled state for over blurred surface
        "&:disabled, &[aria-disabled=true]": {
          background: "rgba(225, 225, 225, 0.1)",
          color: "rgba(150, 150, 150, 0.7)",
        },
      },
      false: {
        // styles are in compound variants
        "&:disabled, &[aria-disabled=true]": {
          color: "$gray.600",
        },
      },
    },
  },

  defaultVariants: {
    variant: "primary",
    rounded: "sm",
    overBlurredSurface: false,
  },

  compoundVariants: [
    {
      variant: ["destructive", "primary", "secondary", "tertiary"],
      overBlurredSurface: false,
      css: {
        "&:disabled, &[aria-disabled=true]": {
          background: "$gray.800",
        },
      },
    },
    {
      variant: ["quarternary", "blur"],
      overBlurredSurface: false,
      css: {
        "&:disabled, &[aria-disabled=true]": {
          background: "transparent",
        },
      },
    },
    {
      variant: ["elevated"],
      overBlurredSurface: false,
      css: {
        "&:disabled, &[aria-disabled=true]": {
          backgroundColor: "$surface.translucent",
        },
      },
    },
  ],
});

export const buttonContentStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  variants: {
    size: {
      xs: {
        textStyle: "body-3-subtle",
        gap: "8px",
      },
      sm: {
        textStyle: "body-3-subtle",
        gap: "8px",
      },
      md: {
        textStyle: "body-3-subtle",
        gap: "10px",
      },
      lg: {
        textStyle: "body-2-subtle",
        gap: "12px",
      },
    },
    isPending: {
      true: {
        visibility: "hidden",
      },
      false: {},
    },
    iconOnly: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      iconOnly: true,
      size: "xs",
      css: {
        width: "28px",
        height: "28px",
      },
    },
    {
      iconOnly: false,
      size: "xs",
      css: {
        padding: "3px 8px",
      },
    },
    {
      iconOnly: true,
      size: "sm",
      css: {
        width: "32px",
        height: "32px",
      },
    },
    {
      iconOnly: false,
      size: "sm",
      css: {
        padding: "6px 12px",
      },
    },
    {
      iconOnly: true,
      size: "md",
      css: {
        width: "40px",
        height: "40px",
      },
    },
    {
      iconOnly: false,
      size: "md",
      css: {
        padding: "10px 16px",
      },
    },
    {
      iconOnly: false,
      size: "lg",
      css: {
        padding: "12px 20px",
      },
    },
  ],

  defaultVariants: {
    size: "sm",
  },
});

const circularProgressStyles = css({
  "& svg": {},
});

const sizeToRounded = (size: "xs" | "sm" | "md" | "lg"): "sm" | "md" | "full" => {
  switch (size) {
    case "xs":
      return "sm";
    case "sm":
      return "sm";
    case "md":
      return "md";
    case "lg":
      return "md";
  }
};
